import type { PosOfflineSale } from '@mlm/shared';
import type { AxiosRequestConfig } from 'axios';
import { useAuthStore } from '../auth-store';
import type { PosCheckoutPayload } from '../api';
import {
  buildOfflineSale,
  localPreview,
  localSaleNumber,
  OfflineRefusal,
  priceMapFor,
  productsForOffline,
  searchDirectory,
  slotsOfMember,
  takeFromStock,
} from './pure';
import { currentPack, deviceId, enqueue, nextSaleSeq, persistPackChange } from './store';

export type LocalAnswer = { status: number; data: unknown };

const ok = (data: unknown): LocalAnswer => ({ status: 200, data });
const created = (data: unknown): LocalAnswer => ({ status: 201, data });
const refused = (code: string): LocalAnswer => ({ status: 400, data: { error: code } });

function paramOf(config: AxiosRequestConfig, name: string): string | null {
  const params = config.params as Record<string, unknown> | undefined;
  const value = params?.[name];
  return value == null ? null : String(value);
}

function bodyOf<T>(config: AxiosRequestConfig): T {
  return (typeof config.data === 'string' ? JSON.parse(config.data) : config.data) as T;
}

/**
 * The server, for the few requests a till needs to keep selling, answered from
 * this device with the shapes the real routes use. That is what lets the till's
 * pages stay as they are: they ask the same questions and get the same kind of
 * answer. Returns null for anything that really needs the server.
 */
export async function answerLocally(config: AxiosRequestConfig): Promise<LocalAnswer | null> {
  const pack = currentPack();
  if (!pack) return null;
  const method = (config.method ?? 'get').toLowerCase();
  const url = (config.url ?? '').split('?')[0]!;
  const roles = useAuthStore.getState().user?.roles ?? [];

  if (method === 'get') {
    if (url === '/tenant/config') return ok(pack.config);
    if (url === '/branches') return ok(pack.branches.filter((b) => b.id === pack.branchId));
    if (url === '/pos/products') return ok(productsForOffline(pack.products));
    if (url === '/pos/course-policy') return ok(pack.coursePolicy);
    if (url === '/pos/discount-presets') return ok({ presets: [], codesEnabled: false });
    if (url === '/pos/vouchers') return ok({ vouchers: [] });
    if (url === '/pos/shift-closes') return ok([]);
    if (url === '/pos/payment-methods') {
      return ok({
        methods: pack.directory.methods.filter(
          (m) =>
            m.roleCodes.length === 0 ||
            roles.includes('super_admin') ||
            m.roleCodes.some((r) => roles.includes(r)),
        ),
      });
    }
    if (url === '/pos/members/search') {
      return ok(searchDirectory(pack.directory.members, paramOf(config, 'q') ?? ''));
    }
    if (url === '/pos/price-map') {
      return ok(
        priceMapFor(
          pack.directory,
          paramOf(config, 'buyerUserId') ?? '',
          paramOf(config, 'buyerSlotId'),
        ),
      );
    }
    const slots = /^\/pos\/users\/([^/]+)\/slots$/.exec(url);
    if (slots) return ok(slotsOfMember(pack.directory, slots[1]!));
    if (/^\/pos\/slots\/[^/]+\/stockist$/.test(url)) return ok(null);
    return null;
  }

  if (method === 'post') {
    if (url === '/pos/checkout/preview') {
      const body = bodyOf<{
        buyerUserId: string;
        buyerSlotId?: string | null;
        items: Array<{ productId: string; variantId?: string; qty: number }>;
      }>(config);
      const prices = priceMapFor(pack.directory, body.buyerUserId, body.buyerSlotId ?? null);
      return ok(localPreview(pack.products, prices, body.items));
    }
    if (url === '/pos/checkout') return checkoutLocally(bodyOf<PosCheckoutPayload>(config));
    if (url === '/pos/shift-close') return closeShiftLocally(bodyOf(config));
  }
  return null;
}

async function checkoutLocally(payload: PosCheckoutPayload): Promise<LocalAnswer> {
  const pack = currentPack();
  const user = useAuthStore.getState().user;
  if (!pack || !user) return refused('offline_not_ready');
  const device = await deviceId();
  const seq = await nextSaleSeq();
  let built: ReturnType<typeof buildOfflineSale>;
  try {
    built = buildOfflineSale({
      payload,
      directory: pack.directory,
      products: pack.products,
      branch: pack.branches.find((b) => b.id === payload.branchId),
      cashier: { id: user.id, fullName: user.fullName, email: user.email, roles: user.roles },
      deviceId: device,
      localNumber: localSaleNumber(device, seq),
      now: new Date(),
    });
  } catch (err) {
    if (err instanceof OfflineRefusal) return refused(err.code);
    throw err;
  }
  // On disk before the till says it went through: this is the only copy.
  await enqueue({
    id: built.sale.clientSaleId,
    kind: 'sale',
    sale: built.sale as PosOfflineSale,
    receipt: built.receipt,
  });
  await persistPackChange({
    ...pack,
    products: takeFromStock(pack.products, payload.items),
  });
  return created(built.receipt);
}

async function closeShiftLocally(body: {
  branchId: string;
  startAt: string;
  actualCash: number;
  notes?: string;
}): Promise<LocalAnswer> {
  const user = useAuthStore.getState().user;
  if (!user) return refused('offline_not_ready');
  const id = `shift-${crypto.getRandomValues(new Uint32Array(4)).join('')}`;
  const closedAt = new Date().toISOString();
  await enqueue({
    id,
    kind: 'shift_close',
    shift: {
      clientId: id,
      deviceId: await deviceId(),
      closedByUserId: user.id,
      branchId: body.branchId,
      startAt: body.startAt,
      closedAt,
      actualCash: body.actualCash,
      notes: body.notes,
    },
  });
  return created({
    offline: true,
    record: {
      id,
      cashierUserId: user.id,
      branchId: body.branchId,
      startAt: body.startAt,
      endAt: closedAt,
      expectedCash: '0',
      actualCash: String(body.actualCash),
      variance: '0',
      notes: body.notes ?? null,
    },
    expectedCash: '0',
    variance: '0',
  });
}
